import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {NodeComponentProps} from 'react-flow-renderer';
import {MeetingRoomProcessedStatus, MeetingRoomStatus, PlanElement, WorkspaceBookType} from '@innowise-group/core';
import * as Styled from './plan-element-view.styles';
import {RoomInfo} from '../room-info';
import stairs from '../../assets/stairs.png';
import lift from '../../assets/lift.png';
import balcony from '../../assets/balcony.png';
import {FloorContext} from '../floor-plan/floor-plan.component';

const FREE_WORKSPACE_STATUSES = new Set<WorkspaceBookType>(['Free', 'Coworking Free']);

const OCCUPIED_WORKSPACE_STATUSES = new Set<WorkspaceBookType>(['Occupied', 'Reserved', 'Booked', 'Coworking Booked']);

const getPlanElementSign = (type) => {
  switch (type) {
    case 'stairs':
      return <Styled.PlanElementSign src={stairs} alt="img" />;
    case 'balconies':
      return <Styled.PlanElementSign src={balcony} alt="img" />;
    case 'lifts':
      return <Styled.PlanElementSign src={lift} alt="img" />;
    default:
      return null;
  }
};

export type MeetingRoomData = MeetingRoomProcessedStatus & {meetingRoomId: string};

export interface PlanElementData extends PlanElement {
  isViewMode?: boolean;
  meetingRoomId?: string;
  capacity?: number;
  meetingRoomStatus?: MeetingRoomStatus;
}

const PlanElementView: React.FC<NodeComponentProps<PlanElementData>> = ({data, selected, type}) => {
  const [width, setWidth] = useState<number>(null);
  const [height, setHeight] = useState<number>(null);

  const {url, number, id, isViewMode, roomType, isOur, workspaceCount, meetingRoomId} = data;

  const meetingRoomData: MeetingRoomData = meetingRoomId
    ? {
        meetingRoomId,
        capacity: data?.capacity,
        ...data?.meetingRoomStatus,
      }
    : null;

  const ref = useRef<HTMLImageElement>(null);

  const isSelectedRoom = useMemo(() => selected && type === 'rooms', [selected, type]);
  const isEditRoom = useMemo(() => isSelectedRoom && !isViewMode, [isSelectedRoom, isViewMode]);
  const canDeleteDoor = useMemo(() => type === 'doors' && isViewMode === false, [type, isViewMode]);

  const {floorsEventEmitter, attendanceStatistics, workspaceElements} = useContext(FloorContext);

  const roomWorkspaceStats = useMemo(() => {
    const roomWorkspaces = (workspaceElements || []).filter(
      (element) => !element.data?.isDraft && String(element.data?.room?.id) === String(id),
    );

    if (!workspaceElements) {
      return {
        total: workspaceCount ?? 0,
        free: 0,
        occupied: 0,
      };
    }

    return {
      total: roomWorkspaces.length,
      free: roomWorkspaces.filter(
        (element) => element.data?.status != null && FREE_WORKSPACE_STATUSES.has(element.data.status),
      ).length,
      occupied: roomWorkspaces.filter(
        (element) => element.data?.status != null && OCCUPIED_WORKSPACE_STATUSES.has(element.data.status),
      ).length,
    };
  }, [workspaceElements, id, workspaceCount]);

  useEffect(() => {
    const img = ref.current;
    if (!img) return;

    const handleImageLoad = () => {
      setWidth(img.naturalWidth);
      setHeight(img.naturalHeight);
    };

    img.onload = handleImageLoad;

    return () => {
      if (img) {
        img.onload = null;
      }
    };
  }, []);

  const handlerEditRoom = useCallback(() => {
    floorsEventEmitter.emit('editRoom', id, roomType, number, isOur, meetingRoomId);
  }, [floorsEventEmitter, id, isOur, meetingRoomId, number, roomType]);

  const handleDeleteDoor = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!canDeleteDoor) {
        return;
      }
      floorsEventEmitter.emit('deleteDoor', id);
    },
    [canDeleteDoor, floorsEventEmitter, id],
  );

  const onDragStartHandler = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <Styled.PlanElementViewContainer width={width} height={height} onDragStart={onDragStartHandler}>
      <Styled.Label selected={isSelectedRoom}>
        {number && (
          <RoomInfo
            id={id}
            workspaceCount={roomWorkspaceStats.total}
            freeWorkspaceCount={roomWorkspaceStats.free}
            occupiedWorkspaceCount={roomWorkspaceStats.occupied}
            roomType={roomType}
            label={number}
            isEditRoom={isEditRoom}
            isEditMode={!isViewMode}
            handlerEditRoom={handlerEditRoom}
            isOur={isOur}
            meetingRoomData={meetingRoomData}
            isRoomSelected={isSelectedRoom}
            attendanceStatistics={attendanceStatistics}
          />
        )}
        {getPlanElementSign(type)}
      </Styled.Label>
      {type === 'rooms' ? (
        <Styled.RoomImage
          src={url}
          ref={ref}
          alt="img"
          crossOrigin="anonymous"
          selected={isSelectedRoom}
          isOur={isOur}
          isEditRoom={isEditRoom}
        />
      ) : type === 'doors' ? (
        <Styled.DoorImage
          src={url}
          ref={ref}
          alt="img"
          crossOrigin="anonymous"
          clickable={canDeleteDoor}
          onClick={handleDeleteDoor}
        />
      ) : (
        <Styled.Image src={url} ref={ref} alt="img" crossOrigin="anonymous" />
      )}
    </Styled.PlanElementViewContainer>
  );
};

export default React.memo(PlanElementView);
